
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getCourierByPhone } from '@/utils/supabase/courier';
import { Payment } from '@/services/types';

interface UseCourierPaymentsDataResult {
  payments: Payment[];
  totalPaid: number;
  pendingAmount: number;
  lastPaymentAmount: number;
  isLoading: boolean;
  error: string | null;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
}

export function useCourierPaymentsData(): UseCourierPaymentsDataResult {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        let courierId: string | null = null;
        
        // Находим курьера по телефону пользователя
        if (user?.phone) {
          const { data: courierData, error: courierError } = await getCourierByPhone(user.phone);
          
          if (courierError) {
            console.error('Ошибка при поиске курьера:', courierError);
          }
          
          if (courierData) {
            courierId = courierData.id;
            console.log('Найден ID курьера по телефону:', courierId);
          }
        }
        
        if (!courierId) {
          console.log('ID курьера не определен');
          setPayments([]);
          setIsLoading(false);
          return;
        }
        
        console.log('Запрашиваем платежи для курьера ID:', courierId);
        
        // Получаем платежи из базы данных
        const { data, error: paymentsError } = await supabase
          .from('payments')
          .select('*, rental:rentals(id, human_number)')
          .eq('courier_id', courierId)
          .order('payment_date', { ascending: false });
          
        if (paymentsError) {
          throw new Error(`Ошибка при загрузке платежей: ${paymentsError.message}`);
        }
        
        if (data) {
          // Преобразуем данные из базы в формат для компонента
          const formattedPayments: Payment[] = data.map(payment => ({
            id: payment.id,
            rentalId: payment.rental_id || '',
            rentalHumanNumber: payment.rental?.human_number || null,
            courierId: payment.courier_id,
            courierName: 'Курьер', // Используем значение по умолчанию
            amount: payment.amount,
            date: payment.payment_date,
            status: payment.status as any,
            method: payment.payment_method as any,
            human_number: payment.human_number,
            receiptUrl: payment.receipt_url,
            subscriptionId: payment.subscription_id
          }));
          
          setPayments(formattedPayments);
          console.log(`Загружено ${formattedPayments.length} платежей для курьера`);
        } else {
          setPayments([]);
        }
      } catch (err: any) {
        console.error('Ошибка при загрузке данных о платежах:', err);
        setError(err.message || 'Не удалось загрузить данные о платежах');
        setPayments([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPayments();
  }, [user]);

  // Вычисляем статистику на основе реальных данных
  const totalPaid = payments
    .filter(payment => payment.status === 'paid' || payment.status === 'completed')
    .reduce((sum, payment) => sum + payment.amount, 0);

  const pendingAmount = payments
    .filter(payment => payment.status === 'pending')
    .reduce((sum, payment) => sum + payment.amount, 0);

  const lastPaymentAmount = payments.length > 0 ? payments[0]?.amount || 0 : 0;

  return {
    payments,
    totalPaid,
    pendingAmount,
    lastPaymentAmount,
    isLoading,
    error,
    statusFilter,
    setStatusFilter
  };
}
